import * as React from 'react';

// Animation primitives: Reveal-on-scroll, CountUp, useIsMobile, FloatingStuds.

export const useIsMobile = (bp = 760) => {
  const [mobile, setMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth <= bp : false
  );
  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${bp}px)`);
    const onChange = (e) => setMobile(e.matches);
    setMobile(mql.matches);
    mql.addEventListener?.('change', onChange);
    mql.addListener?.(onChange); // Safari
    return () => {
      mql.removeEventListener?.('change', onChange);
      mql.removeListener?.(onChange);
    };
  }, [bp]);
  return mobile;
};

// Wrap any block to fade-up-on-scroll-into-view.
export const Reveal = ({ children, delay = 0, as = 'div', style, className = '', once = true }) => {
  const ref = React.useRef(null);
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // If reduced motion: show immediately.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setShown(true);
          if (once) io.unobserve(e.target);
        } else if (!once) {
          setShown(false);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [once]);

  const Tag = as;
  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? 'is-in' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
    >
      {children}
    </Tag>
  );
};

// Count up to a number when it enters the viewport.
export const CountUp = ({ to, duration = 1200, decimals = 0, prefix = '', suffix = '', style }) => {
  const ref = React.useRef(null);
  const [val, setVal] = React.useState(0);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVal(to);
      return;
    }
    let raf;
    let started = false;
    const start = (t0) => {
      const step = (t) => {
        const p = Math.min(1, (t - t0) / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(to * eased);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          start(performance.now());
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [to, duration]);

  const display = decimals > 0 ? val.toFixed(decimals) : Math.round(val);
  return <span ref={ref} style={style}>{prefix}{display}{suffix}</span>;
};

// Floating LEGO studs as decoration, with subtle desktop-only parallax.
export const FloatingStuds = ({ studs }) => {
  const refs = React.useRef([]);
  React.useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const desktop = window.matchMedia?.('(min-width: 980px)').matches;
    if (reduce || !desktop) return;
    let raf = 0;
    let point = { x: 0.5, y: 0.5 };
    const apply = () => {
      refs.current.forEach((el, i) => {
        if (!el) return;
        const s = studs[i];
        const dx = (point.x - 0.5) * (s.size / 3);
        const dy = (point.y - 0.5) * (s.size / 3);
        el.style.setProperty('--px', `${dx.toFixed(2)}px`);
        el.style.setProperty('--py', `${dy.toFixed(2)}px`);
      });
      raf = 0;
    };
    const onMove = (e) => {
      point = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight };
      if (!raf) raf = requestAnimationFrame(apply);
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, [studs]);

  return (
    <>
      {studs.map((s, i) => (
        <span
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          className="stud"
          aria-hidden="true"
          style={{
            width: s.size, height: s.size,
            left: s.left, top: s.top, right: s.right, bottom: s.bottom,
            background: s.color,
            animation: `${['float1','float2','float3'][i % 3]} ${s.speed || 5}s ease-in-out infinite`,
            animationDelay: `${(i * 0.35).toFixed(2)}s`,
            zIndex: 0,
          }}
        />
      ))}
    </>
  );
};

